import type { NodeKinds } from "interfaces/enums";
import type { Folder, Node } from "interfaces/schemas";
import * as treeUtils from "utils/treeUtils";

export const calculateNewNodePosition = (node: Node, parent: Node) => {
  const { nodes: siblings } = parent;
  if (!siblings || (Array.isArray(siblings) && siblings.length === 0)) {
    return 0;
  }

  if (treeUtils.isAnyProduct(node)) {
    let position = 0;
    siblings.forEach((sibling) => {
      if (treeUtils.isAnyProduct(sibling)) {
        position = sibling.position + 1;
      }
    });
    return position;
  }

  return siblings.length;
};

type NodeToUpdate = {
  id: string;
  position: number;
  kind: NodeKinds;
};

type NodesToUpdateMap = Map<string, NodeToUpdate>;

export const updateSiblingsAfterInsert = (
  nodes: Node[],
  newPos: number,
  nodesToUpdate: NodesToUpdateMap,
) => {
  // Update the positions of nodes after the inserted node
  if (newPos + 1 < nodes.length) {
    for (let i = newPos + 1; i < nodes.length; i += 1) {
      nodes[i].position = i;
      if (nodesToUpdate.has(nodes[i].id)) {
        nodesToUpdate.delete(nodes[i].id);
      } else {
        nodesToUpdate.set(nodes[i].id, {
          id: nodes[i].id,
          position: nodes[i].position,
          kind: nodes[i].kind,
        });
      }
    }
  }
};

export const popNodeFromParent = (parent: Folder, nodeId: string) => {
  for (let i = 0; i < parent.nodes.length; i += 1) {
    if (parent.nodes[i].id === nodeId) {
      // Remove the node from its current position
      const [firstNode] = parent.nodes.splice(i, 1);
      return {
        node: firstNode,
        index: i,
      };
    }
  }
};

export const updateSiblingsAfterPop = (
  nodes: Node[],
  removedIndex: number,
  nodesToUpdate: NodesToUpdateMap,
) => {
  for (let i = removedIndex; i < nodes.length; i += 1) {
    // Update the position of nodes after the removed node
    nodes[i].position = i;
    nodesToUpdate.set(nodes[i].id, {
      id: nodes[i].id,
      position: nodes[i].position,
      kind: nodes[i].kind,
    });
  }
};

export const calculatePartiallyVisible = (node: Node) => {
  let numberOfActiveSiblings = 0;
  let partiallyVisible = false;
  if (!node) {
    return false;
  }
  if (treeUtils.isLeafNode(node)) {
    return false;
  }
  for (const sibling of node.nodes || []) {
    if (sibling.active) {
      numberOfActiveSiblings += 1;
    } else if (sibling.partiallyVisible) {
      partiallyVisible = true;
      break;
    }
  }

  if (!partiallyVisible) {
    if (
      numberOfActiveSiblings > 0 &&
      numberOfActiveSiblings !== (node.nodes || []).length
    ) {
      partiallyVisible = true;
    }
  }

  return partiallyVisible;
};
